import { Alert, Button, Card, Drawer, Form, Input, Select, Space, Switch, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, DownOutlined, PlusOutlined, UpOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductFormRenderer from '../components/ProductFormRenderer';
import {
  createAdminIncidentForm,
  listAdminIncidentForms,
  listAdminProducts,
  updateAdminIncidentForm,
} from '../services/api';
import type {
  CurrentUser,
  IncidentFormDefinition,
  IncidentFormField,
  IncidentFormFieldType,
  IncidentFormSchema,
  IncidentFormUpsert,
  ProductMaster,
} from '../types/incident';

const { Text } = Typography;
const { TextArea } = Input;

type AdminFormBuilderPageProps = {
  currentUser: CurrentUser | null;
};

type BuilderFormValues = Omit<IncidentFormUpsert, 'schema_json'> & {
  schema_json: {
    fields: Array<Partial<IncidentFormField> & { options_text?: string }>;
  };
};

const defaultSchema: IncidentFormSchema = { version: 1, fields: [] };
const fieldTypes: IncidentFormFieldType[] = ['text', 'textarea', 'number', 'date', 'select', 'multi_select', 'checkbox', 'section'];

const createEmptyField = (): Partial<IncidentFormField> & { options_text?: string } => ({
  id: `field_${Date.now()}`,
  type: 'text',
  label: '',
  required: false,
  options_text: '',
});

const normalizeField = (field: Partial<IncidentFormField> & { options_text?: string }): IncidentFormField => ({
  id: (field.id || '').trim(),
  type: field.type || 'text',
  label: (field.label || '').trim(),
  description: field.description?.trim() || null,
  required: field.required || false,
  placeholder: field.placeholder?.trim() || null,
  unit: field.unit?.trim() || null,
  options: (field.options_text || '')
    .split('\n')
    .map((option) => option.trim())
    .filter(Boolean),
});

const toFormValues = (record?: IncidentFormDefinition | null): BuilderFormValues => ({
  name: record?.name || '',
  description: record?.description || null,
  is_active: record?.is_active ?? true,
  product_ids: record?.product_ids || [],
  schema_json: {
    fields: (record?.schema_json.fields || []).map((field) => ({
      ...field,
      options_text: field.options.join('\n'),
    })),
  },
});

const AdminFormBuilderPage = ({ currentUser }: AdminFormBuilderPageProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<BuilderFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const [forms, setForms] = useState<IncidentFormDefinition[]>([]);
  const [products, setProducts] = useState<ProductMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingForm, setEditingForm] = useState<IncidentFormDefinition | null>(null);
  const [keyword, setKeyword] = useState('');
  const watchedFields = Form.useWatch(['schema_json', 'fields'], form);

  const isSystemAdmin = useMemo(
    () => currentUser?.assignments.some((assignment) => assignment.role === 'SYSTEM_ADMIN' && assignment.is_active) || false,
    [currentUser?.assignments]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [nextForms, nextProducts] = await Promise.all([listAdminIncidentForms(), listAdminProducts()]);
      setForms(nextForms);
      setProducts(nextProducts);
    } catch {
      messageApi.error(t('adminFormBuilder.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [messageApi, t]);

  useEffect(() => {
    if (isSystemAdmin) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [isSystemAdmin, loadData]);

  const productOptions = useMemo(
    () =>
      products.map((product) => ({
        value: product.id,
        label: `${product.product_name} / ${product.model_number}`,
      })),
    [products]
  );

  const previewSchema = useMemo<IncidentFormSchema>(() => {
    try {
      return {
        version: 1,
        fields: (watchedFields || []).map(normalizeField).filter((field) => field.id && field.label),
      };
    } catch {
      return defaultSchema;
    }
  }, [watchedFields]);

  const filteredForms = useMemo(() => {
    const normalized = keyword.trim().toLowerCase();
    if (!normalized) return forms;
    return forms.filter((item) => `${item.name} ${item.description || ''}`.toLowerCase().includes(normalized));
  }, [forms, keyword]);

  const openCreateDrawer = () => {
    setEditingForm(null);
    form.resetFields();
    form.setFieldsValue(toFormValues(null));
    setDrawerOpen(true);
  };

  const openEditDrawer = (record: IncidentFormDefinition) => {
    setEditingForm(record);
    form.resetFields();
    form.setFieldsValue(toFormValues(record));
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingForm(null);
    form.resetFields();
  };

  const buildPayload = (values: BuilderFormValues): IncidentFormUpsert => ({
    name: values.name.trim(),
    description: values.description?.trim() || null,
    is_active: values.is_active ?? true,
    product_ids: values.product_ids || [],
    schema_json: {
      version: 1,
      fields: (values.schema_json.fields || []).map(normalizeField),
    },
  });

  const handleSubmit = async (values: BuilderFormValues) => {
    setSaving(true);
    try {
      const payload = buildPayload(values);
      const saved = editingForm
        ? await updateAdminIncidentForm(editingForm.id, payload)
        : await createAdminIncidentForm(payload);
      setForms((current) => [saved, ...current.filter((item) => item.id !== saved.id)].sort((a, b) => a.name.localeCompare(b.name)));
      messageApi.success(editingForm ? t('adminFormBuilder.messages.updated') : t('adminFormBuilder.messages.created'));
      closeDrawer();
      await loadData();
    } catch {
      messageApi.error(t('adminFormBuilder.messages.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<IncidentFormDefinition> = [
    {
      title: t('adminFormBuilder.columns.form'),
      key: 'form',
      render: (_value, record) => (
        <Space direction="vertical" size={2}>
          <Text strong>{record.name}</Text>
          {record.description ? <Text type="secondary">{record.description}</Text> : null}
        </Space>
      ),
    },
    {
      title: t('adminFormBuilder.columns.status'),
      dataIndex: 'is_active',
      width: 110,
      render: (isActive: boolean) => <Tag color={isActive ? 'green' : 'default'}>{isActive ? t('common.active') : t('common.inactive')}</Tag>,
    },
    { title: t('adminFormBuilder.columns.fields'), width: 100, render: (_value, record) => record.schema_json.fields.length },
    { title: t('adminFormBuilder.columns.products'), dataIndex: 'assigned_product_count', width: 130 },
    {
      title: t('adminFormBuilder.columns.actions'),
      key: 'actions',
      width: 100,
      render: (_value, record) => (
        <Button size="small" onClick={() => openEditDrawer(record)}>
          {t('adminFormBuilder.actions.edit')}
        </Button>
      ),
    },
  ];

  if (!isSystemAdmin) {
    return (
      <div className="page">
        {contextHolder}
        <Alert type="warning" showIcon message={t('adminFormBuilder.forbidden')} />
      </div>
    );
  }

  return (
    <div className="page adminFormBuilderPage">
      {contextHolder}
      <div className="pageHeader">
        <p>{t('adminFormBuilder.description')}</p>
      </div>

      <Card
        className="adminMasterCard"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateDrawer}>
            {t('adminFormBuilder.actions.create')}
          </Button>
        }
      >
        <Input.Search
          className="adminMasterSearch"
          allowClear
          placeholder={t('master.filters.keyword')}
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
        />
        <Table rowKey="id" columns={columns} dataSource={filteredForms} loading={loading} pagination={{ pageSize: 20 }} />
      </Card>

      <Drawer
        title={editingForm ? t('adminFormBuilder.form.editTitle') : t('adminFormBuilder.form.createTitle')}
        open={drawerOpen}
        onClose={closeDrawer}
        width="88vw"
        destroyOnClose
        extra={
          <Space>
            <Button onClick={closeDrawer}>{t('common.cancel')}</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              {editingForm ? t('adminFormBuilder.actions.update') : t('adminFormBuilder.actions.save')}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={toFormValues(null)}>
          <div className="formBuilderLayout">
            <div className="formBuilderEditor">
              <Card size="small" title={t('adminFormBuilder.form.basic')}>
                <Form.Item name="name" label={t('adminFormBuilder.form.name')} rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="description" label={t('adminFormBuilder.form.description')}>
                  <TextArea rows={2} />
                </Form.Item>
                <Form.Item name="product_ids" label={t('adminFormBuilder.form.products')}>
                  <Select mode="multiple" allowClear showSearch optionFilterProp="label" options={productOptions} />
                </Form.Item>
                <Form.Item name="is_active" label={t('adminFormBuilder.form.active')} valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Card>

              <Card size="small" className="formBuilderFieldsCard" title={t('adminFormBuilder.form.fields')}>
                <Form.List name={['schema_json', 'fields']}>
                  {(fields, { add, remove, move }) => (
                    <Space direction="vertical" className="fullWidthControl" size={12}>
                      {fields.map((field, index) => {
                        const type = form.getFieldValue(['schema_json', 'fields', index, 'type']);
                        return (
                          <Card
                            size="small"
                            key={field.key}
                            className="formBuilderFieldCard"
                            title={`${index + 1}. ${t('adminFormBuilder.form.field')}`}
                            extra={
                              <Space>
                                <Button size="small" icon={<UpOutlined />} disabled={index === 0} onClick={() => move(index, index - 1)} />
                                <Button size="small" icon={<DownOutlined />} disabled={index === fields.length - 1} onClick={() => move(index, index + 1)} />
                                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                              </Space>
                            }
                          >
                            <Space direction="vertical" className="fullWidthControl">
                              <Space.Compact block>
                                <Form.Item name={[field.name, 'type']} rules={[{ required: true }]} className="formBuilderCompactItem">
                                  <Select
                                    options={fieldTypes.map((value) => ({
                                      value,
                                      label: t(`adminFormBuilder.fieldTypes.${value}`),
                                    }))}
                                  />
                                </Form.Item>
                                <Form.Item name={[field.name, 'id']} rules={[{ required: true, pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/ }]} className="formBuilderCompactItem">
                                  <Input placeholder={t('adminFormBuilder.form.fieldId')} />
                                </Form.Item>
                              </Space.Compact>
                              <Form.Item name={[field.name, 'label']} label={t('adminFormBuilder.form.label')} rules={[{ required: true }]}>
                                <Input />
                              </Form.Item>
                              <Form.Item name={[field.name, 'description']} label={t('adminFormBuilder.form.fieldDescription')}>
                                <Input />
                              </Form.Item>
                              {type !== 'section' ? (
                                <>
                                  <Form.Item name={[field.name, 'placeholder']} label={t('adminFormBuilder.form.placeholder')}>
                                    <Input />
                                  </Form.Item>
                                  <Space>
                                    <Form.Item name={[field.name, 'required']} label={t('adminFormBuilder.form.required')} valuePropName="checked">
                                      <Switch />
                                    </Form.Item>
                                    {type === 'number' ? (
                                      <Form.Item name={[field.name, 'unit']} label={t('adminFormBuilder.form.unit')}>
                                        <Input />
                                      </Form.Item>
                                    ) : null}
                                  </Space>
                                </>
                              ) : null}
                              {type === 'select' || type === 'multi_select' ? (
                                <Form.Item name={[field.name, 'options_text']} label={t('adminFormBuilder.form.options')} rules={[{ required: true }]}>
                                  <TextArea rows={4} placeholder={t('adminFormBuilder.form.optionsPlaceholder')} />
                                </Form.Item>
                              ) : null}
                            </Space>
                          </Card>
                        );
                      })}
                      <Button block icon={<PlusOutlined />} onClick={() => add(createEmptyField())}>
                        {t('adminFormBuilder.actions.addField')}
                      </Button>
                    </Space>
                  )}
                </Form.List>
              </Card>
            </div>
            <Card className="formBuilderPreview" title={t('adminFormBuilder.preview.title')}>
              <Form layout="vertical">
                <ProductFormRenderer schema={previewSchema} disabled />
              </Form>
            </Card>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default AdminFormBuilderPage;
